import React from 'react';
import { Input as AntdInput, InputProps as AntdInputProps, Button, ConfigProvider } from 'antd';
import {
    SearchProps as AntdSearchProps,
    GroupProps as AntdGroupProps,
    PasswordProps as AntdPasswordProps,
    TextAreaProps as AntdTextAreaProps,
} from 'antd/es/input';
import { OTPProps as AntOTPProps } from 'antd/es/input/OTP';
import { useTheme } from '../../theme/ThemeContext';
import { CloseCircleFilled, SearchOutlined } from '@ant-design/icons';

// Define the props type for your custom Input component
export interface InputProps extends AntdInputProps {}

// Define the props type for the custom Input.Group component
export interface GroupProps extends AntdGroupProps { 'data-test'?: string; 'data-test-id'?: string; }

// Define the props type for the custom Input Search component
export interface SearchProps extends AntdSearchProps {}

// Define the props type for the custom Input Text Area component
export interface TextAreaProps extends AntdTextAreaProps { 'data-test'?: string; 'data-test-id'?: string; }

// Define the props type for the custom Input Password component
export interface PasswordProps extends AntdPasswordProps {}

// Define the props type for the custom Input OTP component
export interface OTPProps extends AntOTPProps { 'data-test'?: string; 'data-test-id'?: string; }

// Wrapper component for Checkbox
const Input: React.FC<InputProps> & {
    Group: React.FC<GroupProps>;
    Search: React.FC<SearchProps>;
    TextArea: React.FC<TextAreaProps>;
    Password: React.FC<PasswordProps>;
    OTP: React.FC<OTPProps>;
} = (props) => {
    const theme = useTheme();
    return (
        <ConfigProvider theme={theme}>
            <AntdInput
                {...props}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-input
                allowClear={
                    props.allowClear
                        ? { clearIcon: <CloseCircleFilled data-test-id={`${props['data-test-id']}-clear-icon`} /> }
                        : false
                }
            />
        </ConfigProvider>
    );
};

// Wrapper component for Input.Group
const InputGroup: React.FC<GroupProps> = (props) => {
    const theme = useTheme();
    return (
        <ConfigProvider theme={theme}>
            <AntdInput.Group {...props} data-test={props['data-test']} data-test-id={props['data-test-id']} data-asper-input-group/>
        </ConfigProvider>
    );
};

// Wrapper component for Input.Search
const InputSearch: React.FC<SearchProps> = (props) => {
    const theme = useTheme();
    return (
        <ConfigProvider theme={theme}>
            <AntdInput.Search {...props} data-test={props['data-test']} data-test-id={props['data-test-id']} data-asper-input-search  allowClear={
                    props.allowClear
                        ? { clearIcon: <CloseCircleFilled data-test-id={`${props['data-test-id']}-clear-icon`} /> }
                        : false
                }
                enterButton={
                    props.enterButton === true ? (
                        <Button type="default" data-test-id={`${props['data-test-id']}-search-button`}>
                            <SearchOutlined />
                        </Button>
                    ) : (
                        props.enterButton
                    )
                }
                />
        </ConfigProvider>
    );
};

// Wrapper component for Input.TextArea
const InputTextArea: React.FC<TextAreaProps> = (props) => {
    const theme = useTheme();
    return (
        <ConfigProvider theme={theme}>
            <AntdInput.TextArea {...props} data-test={props['data-test']} data-test-id={props['data-test-id']} data-asper-input-textarea/>
        </ConfigProvider>
    );
};

// Wrapper component for Input.Password
const InputPassWord: React.FC<PasswordProps> = (props) => {
    const theme = useTheme();
    return (
        <ConfigProvider theme={theme}>
            <AntdInput.Password {...props} data-test={props['data-test']} data-test-id={props['data-test-id']} data-asper-input-otp/>
        </ConfigProvider>
    );
};

// Wrapper component for Input.OTP
const InputOTP: React.FC<OTPProps> = (props) => {
    const theme = useTheme();
    return (
        <ConfigProvider theme={theme}>
            <AntdInput.OTP {...props} data-test={props['data-test']} data-test-id={props['data-test-id']}/>
        </ConfigProvider>
    );
};

// Attach the Group component to the Input component
Input.Group = InputGroup;
Input.Search = InputSearch;
Input.TextArea = InputTextArea;
Input.Password = InputPassWord;
Input.OTP = InputOTP;

export default Input;
